import '../../app/mockups/_styles/editorial.css'
import { ProductGridCard } from "./product-grid-card"
import { badgesFromRecommended } from "@/components/badges/from-product"
import { formatPrice } from "@/lib/format-utils"

interface SidebarPickProduct {
  id: string | number
  brand: string
  title: string
  images?: string[]
  price?: number
  originalPrice?: number
  rating?: number
  reviews?: number
  canonical_url?: string
}

interface SidebarPickProps {
  /** The product to render in the card */
  product: SidebarPickProduct
  /**
   * Eyebrow label above the card. Defaults to "Editor's pick" — the editorial
   * blog template uses this rail for editor-curated picks; pass `eyebrow` to
   * customise (e.g. "Top rated", "Trending in PA").
   */
  eyebrow?: string
}

/**
 * SidebarPick — ProductGridCard wrapped with a small uppercase blue eyebrow
 * label (default "Editor's pick"). Used in editorial sidebar rails on the
 * blog homepage and blog post pages where each pick reads as a curated
 * recommendation rather than a generic product tile.
 *
 * Visual: orange dot + spaced-uppercase eyebrow at 10px / 800 / 0.18em
 * letter-spacing, then the standard `ProductGridCard` directly underneath.
 * The wrapper class `.ed-pick-card-wrap` is used by the blog mockup CSS to
 * stretch the card to 100% width inside narrow column contexts.
 */
export function SidebarPick({ product, eyebrow = "Editor’s pick" }: SidebarPickProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--fridge-blue)",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: "var(--fridge-blue)",
            display: "inline-block",
          }}
        />
        {eyebrow}
      </span>
      <div className="ed-pick-card-wrap">
        <ProductGridCard
          images={product.images || []}
          brand={product.brand}
          title={product.title}
          price={
            typeof product.price === "number" && product.price > 0
              ? formatPrice(product.price)
              : undefined
          }
          originalPrice={
            typeof product.originalPrice === "number" &&
            product.originalPrice > (product.price || 0)
              ? `$${product.originalPrice.toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                })}`
              : undefined
          }
          rating={product.rating}
          href={product.canonical_url || `/products/${product.id}`}
          alt={product.title ? `${product.title} | Fridge.com` : ''}
          badges={badgesFromRecommended(product)}
        />
      </div>
    </div>
  )
}
